/**
 * TOGGLE_LANGUAGES_BAR_V3
 * ------------------------
 * ES5 + jQuery component for rendering and managing a responsive
 * language selector with collapse/expand behavior, active state management,
 * and persistent selection via LocalStorage.
 */
var TOGGLE_LANGUAGES_BAR_V3 = {
    /** @type {jQuery|null} - Main container element */
    container: null,

    /** @type {jQuery|null} - Collapse/expand trigger button */
    collapseButton: null,

    /** @type {boolean} - Current expansion state */
    isExpanded: false,

    /** @type {string} - World icon */
    worldIcon: '<i class="tvgIcon tvgIconLanguage"></i>',

    /** @type {string} - Collapse arrow icon */
    collapseArrowIcon: '<i class="tvgIcon tvgIconArrowDown collapseLanguageList"></i>',

    /** @type {Array} - Language data to render */
    languageData: [],

    /**
     * Initialize the component.
     * @param {Object} options
     * @param {string} options.containerSelector - Selector for the container where the list will be rendered.
     * @param {string} [options.collapseButtonSelector] - Optional selector for collapse/expand button.
     * @param {Array} options.languageArray - Array of languages to display.
     */
    init: function(options) {
        if (!options || !options.containerSelector || !options.languageArray) {
            console.warn('TOGGLE_LANGUAGES_BAR_V3: missing required options');
            return;
        }

        this.container = $(options.containerSelector);
        this.languageData = options.languageArray;

        if (options.collapseButtonSelector) {
            this.collapseButton = $(options.collapseButtonSelector);
        }

        // Restore saved state or default to false
        var savedState = localStorage.getItem('toggleBarExpanded');
        this.isExpanded = savedState === 'true';

        // Apply initial expanded class
        this.container.closest('.toggleLanguageV3Wrapper')
            .toggleClass('expanded', this.isExpanded);

        // Initial render of the language list
        this.render();

        // Bind all UI interactions
        this.attachEvents();
    },

    /**
     * Attach all event listeners for:
     * - Collapse/Expand toggle
     * - Window resize (for responsive re-render)
     * - Language button click (to select + persist language)
     */
    attachEvents: function() {
        var self = this;
        var resizeTimer;

        // Collapse / Expand button
        this.container.on('click', '.collapseLanguageList', function() {
            self.toggle();
        });

        // Handle window resize (debounced)
        $(window).on('resize', function() {
            clearTimeout(resizeTimer);
            resizeTimer = setTimeout(function() {
                self.render();
            }, 200);
        });

        // Language button click handler
        this.container.on('click', '.language-button', function() {
            var $btn = $(this);
            var langCode = $btn.data('lang-code');

            // Update visual active state
            self.container.find('.language-button').removeClass('active');
            $btn.addClass('active');

            // Save selected language to LocalStorage
            try {
                localStorage.setItem('LangSelectedForContent', langCode);
            } catch (e) {
                console.warn('Error saving selected language:', e);
            }

            // Optional: trigger global event for external listeners
            $(document).trigger('language:selected', [langCode]);
        });
    },

    /**
     * Render the entire language list into the container.
     * Uses responsive grid and current expansion state.
     */
    render: function() {
        if (!this.container) return;

        this.container.empty();

        var maxToShow = this.isExpanded
            ? this.languageData.length
            : this.getMaxLanguagesForViewport();

        var $ul = this.buildLanguageList(this.languageData, maxToShow, this.isExpanded);
        this.container.append($ul);
        this.applyDynamicGrid($ul);
    },

    /**
     * Build the <ul> element containing all visible language items.
     * Adds pipe separators where appropriate and marks the selected language as active.
     * @param {Array} languageArray - Full list of available languages.
     * @param {number} maxToShow - Number of languages to display (collapsed/expanded).
     * @param {boolean} isExpanded - Whether the list is expanded.
     * @returns {jQuery} <ul> element ready to be appended.
     */
    buildLanguageList: function(languageArray, maxToShow, isExpanded) {
        var $ul = $('<ul>').addClass('languageList');
        var renderItems = languageArray.slice(0, maxToShow);
        var width = window.innerWidth;
        var maxPerRow = this.getMaxLanguagesForViewport();

        // Retrieve selected language from LocalStorage (fallback to EN_US)
        var selectedLang;
        try {
            selectedLang = localStorage.getItem('LangSelectedForContent') || 'EN_US';
        } catch (e) {
            console.warn('Error reading selected language from LocalStorage:', e);
            selectedLang = 'EN_US';
        }

        for (var i = 0; i < renderItems.length; i++) {
            var lang = renderItems[i];
            var isActive = (lang.code === selectedLang);
            var isFirst = (i === 0);
            var isEndOfFirstRow = (i === maxPerRow - 1);

            // Show pipes before items (tablet/desktop only), skip first item
            var showPipe = (width >= 640 && !isFirst);

            $ul.append(this.createLanguageListItem(lang, isActive, showPipe, isFirst, isEndOfFirstRow));
        }

        return $ul;
    },

    /**
     * Create a <li> element containing a language button (and optional pipe separator).
     * @param {Object} lang - Language object { code, description, id, etc. }.
     * @param {boolean} isActive - Whether this language is currently active.
     * @param {boolean} showPipe - Whether to display a pipe separator after this item.
     * @returns {jQuery} <li> element.
     */
    createLanguageListItem: function(lang, isActive, showPipe, isFirst, isEndOfFirstRow) {
        var $flexWrapper = $('<div>')
            .addClass('li-flex-wrapper')
            .css({ display: 'flex', alignItems: 'center' });

        var $button = this.createLanguageButton(lang, isActive);

        if (isFirst) {
            $flexWrapper.append($(this.worldIcon));
        }

        if (showPipe) {
            $flexWrapper.append(this.createPipeSeparator());
        }

        $flexWrapper.append($button);

        if (isEndOfFirstRow) {
            $flexWrapper.append($(this.collapseArrowIcon));
        }

        return $('<li>').append($flexWrapper);
    },

    /**
     * Create a single language button.
     * @param {Object} lang - Language data.
     * @param {boolean} isActive - Whether it's currently active.
     * @returns {jQuery} <button> element.
     */
    createLanguageButton: function(lang, isActive) {
        return $('<button>')
            .addClass('language-button jsTrigger')
            .toggleClass('active', isActive)
            .attr({
                'data-lang-code': lang.code,
                type: 'button'
            })
            .text(lang.description)
            .css({ flex: '1', textAlign: 'center' });
    },

    /**
     * Create a pipe (|) separator between languages.
     * @returns {jQuery} <div> separator element.
     */
    createPipeSeparator: function() {
        return $('<div>')
            .addClass('pipe-separator')
            .attr('aria-hidden', 'true')
            .text('|')
            .css({ flex: '0 0 auto', marginLeft: '8px' });
    },

    /**
     * Determine how many languages fit per row based on viewport width.
     * @returns {number} - Maximum number of languages per row.
     */
    getMaxLanguagesForViewport: function() {
        var width = window.innerWidth;
        var config = [
            { minWidth: 1536, maxLanguages: 10 },
            { minWidth: 1280, maxLanguages: 8 },
            { minWidth: 640,  maxLanguages: 5 },
            { minWidth: 0,    maxLanguages: Infinity } // Mobile (no restriction)
        ];

        for (var i = 0; i < config.length; i++) {
            if (width >= config[i].minWidth) {
                return config[i].maxLanguages;
            }
        }

        return Infinity;
    },

    /**
     * Apply responsive CSS grid layout to the <ul>.
     * @param {jQuery} $ul - The rendered list.
     */
    applyDynamicGrid: function($ul) {
        var width = window.innerWidth;
        var renderedCount = $ul.find('li').length;
        var maxCols = this.getMaxLanguagesForViewport();
        var cols;

        if (width < 640) {
            cols = 2; // Mobile layout (2 columns)
        } else {
            if (this.isExpanded) {
                cols = Math.min(this.languageData.length || renderedCount, maxCols);
            } else {
                cols = Math.max(1, renderedCount);
            }
        }

        $ul.css({
            display: 'grid',
            'grid-template-columns': 'repeat(' + cols + ', minmax(0, 1fr))',
            gap: (width < 640 ? '0.5rem' : '0.75rem')
        });
    },

    /**
     * Toggle expanded/collapsed state programmatically.
     */
    toggle: function() {
        this.isExpanded = !this.isExpanded;
        localStorage.setItem('toggleBarExpanded', this.isExpanded ? 'true' : 'false');

        this.container.closest('.toggleLanguageV3Wrapper')
            .toggleClass('expanded', this.isExpanded);
        this.render();
    },

    /**
     * Refresh the list with a new set of language data.
     * @param {Array} newData - New language array.
     */
    refresh: function(newData) {
        if (newData) this.languageData = newData;
        this.render();
    }
};

var LANGUAGE_FETCHER_V3 = {
    endpoint: null,
    storageKey: "availableLanguages",

    /**
     * Initialize the fetcher
     * @param options { endpoint: string, storageKey?: string }
     */
    init: function(options) {
        if (!options || !options.endpoint) {
            console.warn("LANGUAGE_FETCHER_V3: missing endpoint in init()");
            return;
        }

        this.endpoint = options.endpoint;

        if (options.storageKey) {
            this.storageKey = options.storageKey;
        }

        console.log("LANGUAGE_FETCHER_V3 initialized with endpoint:", this.endpoint);
    },

    /**
     * Check if languages exist in LocalStorage
     * @returns {Array|null} parsed array of languages or null
     */
    checkCache: function() {
        try {
            var stored = localStorage.getItem(this.storageKey);
            if (stored) {
                return JSON.parse(stored);
            }
        } catch (e) {
            console.warn("Error accessing LocalStorage:", e);
        }
        return null;
    },

    /**
     * Fetch languages from API and save to LocalStorage
     * @param callback {Function} called with array of languages
     * @param errorCallback {Function} optional error handler
     */
    fetch: function(callback, errorCallback) {
        var self = this;

        // Validate endpoint
        if (!self.endpoint) {
            console.warn("LANGUAGE_FETCHER_V3: Missing endpoint URL.");
            if (typeof errorCallback === "function") errorCallback("Missing endpoint");
            return;
        }

        $.ajax({
            url: self.endpoint,
            method: "GET",
            dataType: "json",
            success: function(data) {
                try {
                    localStorage.setItem(self.storageKey, JSON.stringify(data));
                    console.log("Languages fetched from API and saved to cache.");
                    if (typeof callback === "function") callback(data);
                } catch (e) {
                    console.error("Error saving data to LocalStorage:", e);
                    if (typeof callback === "function") callback(data);
                }
            },
            error: function(jqXHR, textStatus, errorThrown) {
                console.error("API Fetch failed:", textStatus, errorThrown);
                if (typeof errorCallback === "function") errorCallback(textStatus, errorThrown);
            }
        });
    },

    /**
     * Clear cached language data from LocalStorage
     */
    clearCache: function() {
        try {
            localStorage.removeItem(this.storageKey);
            console.log("LANGUAGE_FETCHER_V3 cache cleared.");
        } catch (e) {
            console.warn("Error clearing LocalStorage cache:", e);
        }
    }
};